import { Box, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { APP_DEFAULT_EXPORT_PAGE_SIZE } from 'app/appConstants';
import { AxiosResponse } from 'axios';
import { MprcCard, MprcChipChoice, MprcContainer, MprcIconButton, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { MprcFilterLine } from 'components/filter';
import { MprcCardFilterBox } from 'components/filter/MprcCardFilterBox';
import { IconBlocked } from 'components/icons';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { MprcSearchTextField } from 'components/MprcSearchTextField';
import { MprcSwitch } from 'components/MprcSwitch';
import { GetGeneralUnitsHierarchicalPayload, GetGeneralUnitsPayload, TrainingIndexOrderBy, TrainingIndexUnitDtoPaginatedList, TrainingIndexUnitsHierarchicalResponse } from 'generated/CoreApi_v1';
import HtmlUtils from 'lib/HtmlUtils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrainingIndexUnitsFlatList } from 'views/dashboard/TrainingIndex/components/TrainingIndexUnits/TrainingIndexUnitsFlatList';
import { TrainingIndexUnitsHierarchicalList } from 'views/dashboard/TrainingIndex/components/TrainingIndexUnits/TrainingIndexUnitsHierarchicalList';
import { TrainingIndexProps } from 'views/dashboard/TrainingIndex/TrainingIndexRoot';

////////////////////////////////////////////////////////////////////////////
export interface TrainingIndexUnitsFilter {
    searchText: string;
    orderBy: TrainingIndexOrderBy;
    onlyDeleted: boolean;
}

interface IProps extends TrainingIndexProps {
    unitTypeName: string;
    onDrillDown: (unitId: string, name: string) => void;
    getNonAggreateApi: (data: GetGeneralUnitsPayload) => Promise<AxiosResponse<TrainingIndexUnitDtoPaginatedList>>;
    getAggreateApi: (data: GetGeneralUnitsHierarchicalPayload) => Promise<AxiosResponse<TrainingIndexUnitsHierarchicalResponse>>;
    exportApi?: (data: GetGeneralUnitsPayload) => Promise<AxiosResponse<any>>;
    showDeletedFilter?: boolean;

}

////////////////////////////////////////////////////////////////////////////

export const TrainingIndexUnits: React.FC<IProps> = ({ currentMonth, unitTypeName, showDeletedFilter = false, onDrillDown, exportApi, getNonAggreateApi, getAggreateApi }) => {

    const { t } = useTranslation("dashboard");
    const theme = useTheme();

    const [downloading, setDownloading] = useState<boolean>(false);
    const [error, setError] = useState<any>();
    const [aggregate, setAggregate] = useState<boolean>(true);
    const [filter, setFilter] = useState<TrainingIndexUnitsFilter>({
        orderBy: TrainingIndexOrderBy.Best,
        searchText: "",
        onlyDeleted: false,
    });

    const handleFilter = (filter: Partial<TrainingIndexUnitsFilter>) => {
        setFilter(state => ({ ...state, ...filter }));
    };

    const switchAggregate = () => {
        setAggregate(!aggregate);
        handleFilter({ searchText: "" });
    };

    ///
    const switchOnlyDeleted = () => {
        const newValue = !filter.onlyDeleted;
        handleFilter({ onlyDeleted: newValue });

        if (newValue == true && aggregate) switchAggregate();
    };

    ///
    const handleExportExcel = async () => {
        if (!exportApi) return;
        setDownloading(true);
        return HtmlUtils.downloadBlobFromPromise(exportApi({
            yearMonth: currentMonth,
            searchText: "",
            orderBy: filter.orderBy,
            pageNumber: 1,
            pageSize: APP_DEFAULT_EXPORT_PAGE_SIZE,
            onlyDeleted: false
        })
            .catch(MprcRequestSnackbarErrorReport)
            .finally(() => setDownloading(false)));
    };


    return (
        error
            ? <MprcRequestErrorReport error={error} />
            : <MprcCard>
                <MprcRequestIndicator type="downloading" visible={downloading} />
                <MprcContainer margin="dense">
                    <MprcCardFilterBox>
                        <Box px={theme.spacing(2)}>
                            <MprcFilterLine>
                                <Typography variant="h2">{t("TrainingIndex.IndexByUnitTitle", { name: unitTypeName })}</Typography>
                                <div>
                                    <MprcChipChoice
                                        label={t("TrainingIndex.BestResultsOption")}
                                        uppercase
                                        selected={filter.orderBy == TrainingIndexOrderBy.Best}
                                        onClick={() => handleFilter({ orderBy: TrainingIndexOrderBy.Best })}
                                    />
                                    <MprcChipChoice
                                        label={t("TrainingIndex.WorstResultsOption")}
                                        uppercase
                                        selected={filter.orderBy == TrainingIndexOrderBy.Worst}
                                        onClick={() => handleFilter({ orderBy: TrainingIndexOrderBy.Worst })}
                                    />
                                    {showDeletedFilter &&
                                        <MprcIconButton
                                            sx={{ ml: 1 }}
                                            title={t("TrainingIndex.OnlyDeletedUnitsToolip")}
                                            color={filter.onlyDeleted ? "warning" : "muted"}
                                            onClick={switchOnlyDeleted}
                                        >
                                            <IconBlocked size="sm" />
                                        </MprcIconButton>
                                    }
                                </div>
                                <Stack direction="row" width={150} justifyContent="flex-end">
                                    <MprcSwitch
                                        disabled={filter.onlyDeleted}
                                        checked={aggregate}
                                        onChange={switchAggregate}
                                        label={aggregate
                                            ? t("TrainingIndex.AgregatteText")
                                            : t("TrainingIndex.NotAgregatteText")
                                        }
                                    />
                                </Stack>
                            </MprcFilterLine>
                            <MprcContainer>
                                <Typography variant="caption">{t("TrainingIndex.IndexByUnitHint")}</Typography>
                            </MprcContainer>
                            <MprcSearchTextField
                                initialValue={filter.searchText}
                                confirmMode="onEnter"
                                placeholder={t("TrainingIndex.SearchTextPlaceholder")}
                                onConfirm={(value) => {
                                    handleFilter({ searchText: value });
                                }}
                            />
                        </Box>
                    </MprcCardFilterBox>
                </MprcContainer>
                <CardContent sx={{ pt: 1 }}>
                    {aggregate ?
                        <TrainingIndexUnitsHierarchicalList
                            currentMonth={currentMonth}
                            getApi={getAggreateApi}
                            filter={filter}
                            onDrillDownItem={(unitId, name) => onDrillDown(unitId, name)}
                            onResetSearch={() => handleFilter({ searchText: "" })}
                            onError={(e) => setError(e)}
                            onExportData={handleExportExcel}
                        />
                        :
                        <TrainingIndexUnitsFlatList
                            currentMonth={currentMonth}
                            getApi={getNonAggreateApi}
                            filter={filter}
                            onDrillDownItem={(unitId, name) => onDrillDown(unitId, name)}
                            onResetSearch={() => handleFilter({ searchText: "" })}
                            onError={(e) => setError(e)}
                            onExportData={handleExportExcel}
                        />
                    }
                </CardContent>
            </MprcCard >
    );
};
